import React, { useState, useRef, useEffect, useMemo } from "react";
import { Box, IconButton, Tooltip, alpha, Button, Typography, TextField, Menu, MenuItem } from "@mui/material";
import CodeMirror from "@uiw/react-codemirror";
import { ModalOverlay, ModalContainer } from "../Modal/Modal.styled";
import { sql } from "@codemirror/lang-sql";
import { EditorView } from "@codemirror/view";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ReplayIcon from "@mui/icons-material/Replay";
import CheckIcon from "@mui/icons-material/Check";
import { useAuth } from "../../hooks/useAuth";
import { useRbac } from "../../hooks/RbacContext";
import { StorageHelper } from "../../utils/storageHelper";
import { useSqlResults } from "../../contexts/SqlResultsContext";
import { useGeneratedSqlFeedback } from "../../hooks/useGeneratedSqlFeedback";
import { useAskFlashDiceRunQuery } from "../../hooks/useAskFlashDiceRunQuery";

// Theme constants matching the app's design system
const THEME = {
  primary: "rgb(44, 52, 123)",
  primaryRgba: (a) => `rgba(44, 52, 123, ${a})`,
  hover: "rgb(35, 42, 100)",
  background: {
    default: "#ffffff",
    readOnly: "#fafbfc",
    toolbar: "#f8fafc",
  },
  border: {
    default: "#e2e8f0",
    light: "#e5e7eb",
  },
  text: {
    primary: "#334155",
    secondary: "#64748b",
    muted: "#94a3b8",
  },
  success: "#22c55e",
  warning: "#f59e0b",
};

// Simple hash function to generate unique ID for SQL blocks
const generateSqlBlockId = (code) => {
  let hash = 0;
  for (let i = 0; i < code.length; i++) {
    const char = code.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return `sql_block_${Math.abs(hash)}`;
};

// SessionStorage key prefix
const STORAGE_PREFIX = "sql_editor_edit_";
const EDIT_MODE_PREFIX = "sql_editor_mode_";

// SQL formatting function
const formatSqlQuery = (query) => {
  if (!query) return "";
  let cleaned = query.replace(/"{3,}/g, '"').replace(/"{2,}/g, '"');
  let formatted = cleaned
    .replace(
      /\b(SELECT|FROM|WHERE|GROUP BY|ORDER BY|HAVING|JOIN|LEFT JOIN|RIGHT JOIN|INNER JOIN|OUTER JOIN|UNION|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP)\b/gi,
      "\n$1"
    )
    .replace(
      /\b(AND|OR|IN|NOT IN|LIKE|NOT LIKE|IS NULL|IS NOT NULL|BETWEEN|EXISTS|NOT EXISTS)\b/gi,
      "\n$1"
    )
    .replace(/,/g, ",\n")
    .replace(/\(/g, "(\n")
    .replace(/\)/g, "\n)")
    .replace(/\n\s*\n/g, "\n")
    .split("\n")
    .map((line) => {
      const t = line.trim();
      if (!t) return "";
      let indent = 2;
      if (t.startsWith(")")) indent = 0;
      else if (
        t.match(
          /^(FROM|WHERE|GROUP BY|ORDER BY|HAVING|JOIN|LEFT JOIN|RIGHT JOIN|INNER JOIN|OUTER JOIN|UNION)/i
        )
      )
        indent = 0;
      else if (
        t.match(
          /^(AND|OR|IN|NOT IN|LIKE|NOT LIKE|IS NULL|IS NOT NULL|BETWEEN|EXISTS|NOT EXISTS)/i
        )
      )
        indent = 2;
      else if (t.startsWith(",")) indent = 2;
      else if (t.startsWith("(")) indent = 0;
      return "  ".repeat(indent) + t;
    })
    .filter(Boolean)
    .join("\n");
  return formatted;
};

const SqlCodeBlock = ({ code, language, question, showFeedbackIcons = true, showRetryForThisMessage = true, nlp_message_id: nlpMessageId, messageFeedback, messageModelId = null, onFeedbackSuccess, onRetry, modelOptions = [], selectedModel = "", isLoading }) => {
  // Generate a stable ID for this SQL block based on original code
  const blockId = useMemo(() => generateSqlBlockId(code), [code]);
  const { addGeneratedSqlFeedback } = useGeneratedSqlFeedback();
  const initialFeedback = messageFeedback === "good" ? "up" : messageFeedback === "poor" ? "down" : null;
  const [feedbackSent, setFeedbackSent] = useState(initialFeedback); // "up" | "down" | null
  const [feedbackError, setFeedbackError] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [thumbUpModalOpen, setThumbUpModalOpen] = useState(false);
  const [thumbDownModalOpen, setThumbDownModalOpen] = useState(false);
  const [expectedSqlModal, setExpectedSqlModal] = useState("");
  const [thumbUpComment, setThumbUpComment] = useState("");
  const [thumbDownComment, setThumbDownComment] = useState("");
  const [retryMenuAnchor, setRetryMenuAnchor] = useState(null);

  // Restore edited SQL and edit mode from sessionStorage on mount
  const getStoredEdit = () => {
    try {
      const stored = sessionStorage.getItem(STORAGE_PREFIX + blockId);
      const storedMode = sessionStorage.getItem(EDIT_MODE_PREFIX + blockId);
      if (stored && storedMode === "true") {
        return { sql: stored, isEditMode: true };
      }
      // Check if user explicitly set edit mode to false
      if (storedMode === "false") {
        return { sql: code, isEditMode: false };
      }
    } catch (e) {
      console.warn("Failed to read from sessionStorage:", e);
    }
    // Default to read-only mode
    return { sql: code, isEditMode: false };
  };

  const initialEdit = getStoredEdit();
  const [isEditMode, setIsEditMode] = useState(initialEdit.isEditMode);
  const [editedSql, setEditedSql] = useState(initialEdit.sql);
  const [copied, setCopied] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isFormatted, setIsFormatted] = useState(false); // Track if SQL is currently formatted
  const originalSqlRef = useRef(code); // Store original SQL for revert
  const { user } = useAuth();
  const { checkAccess } = useRbac();
  const canGiveFeedback = checkAccess("askFlashdice", "feedback");
  const canRetry = checkAccess("askFlashdice", "retry");
  const canExecuteQuery = checkAccess("askFlashdice", "execute");
  const canVerifySql = checkAccess("askFlashdice", "verify");
  const canEditSql = checkAccess("askFlashdice", "edit");
  const canFormatSql = checkAccess("askFlashdice", "format");
  const canCopySql = checkAccess("askFlashdice", "copy");
  const { setSqlResults } = useSqlResults();
  const { runQuery } = useAskFlashDiceRunQuery();
  const editorRef = useRef(null);
  const isInitialMount = useRef(true);
  const isEditModeRef = useRef(isEditMode);

  // Feedback must be attributed to the model that generated this message,
  // not whatever model is currently selected in the UI dropdown.
  const generatedByModelId = messageModelId || selectedModel || null;

  useEffect(() => {
    isEditModeRef.current = isEditMode;
  }, [isEditMode]);

  // Save edited SQL to sessionStorage whenever it changes (and we're in edit mode)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    if (isEditMode) {
      try {
        sessionStorage.setItem(STORAGE_PREFIX + blockId, editedSql);
        sessionStorage.setItem(EDIT_MODE_PREFIX + blockId, "true");
      } catch (e) {
        console.warn("Failed to save to sessionStorage:", e);
      }
    }
  }, [editedSql, isEditMode, blockId]);

  // Sync with code prop changes only if not in edit mode and no stored edit exists
  useEffect(() => {
    if (!isEditMode) {
      const stored = sessionStorage.getItem(STORAGE_PREFIX + blockId);
      if (!stored) {
        // Only update if there's no stored edit
        setEditedSql(code);
        originalSqlRef.current = code; // Update original SQL reference
        setIsFormatted(false); // Reset format state when code changes
      }
    }
  }, [code, isEditMode, blockId]);

  // Sync thumb state when message feedback is set (e.g. from loaded chat)
  useEffect(() => {
    const next = messageFeedback === "good" ? "up" : messageFeedback === "poor" ? "down" : null;
    setFeedbackSent((prev) => (prev !== next ? next : prev));
  }, [messageFeedback]);

  // Only render as SQL editor if language is 'sql'
  if (language !== "sql") {
    return (
      <pre style={{ background: "#f8fafc", padding: "16px", borderRadius: "4px", overflow: "auto" }}>
        <code>{code}</code>
      </pre>
    );
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(isEditMode ? editedSql : code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const handleFormat = () => {
    const currentSql = isEditMode ? editedSql : code;
    const originalSql = originalSqlRef.current;
    
    if (isFormatted) {
      // Currently formatted, revert to original
      if (isEditMode) {
        setEditedSql(originalSql);
        if (editorRef.current) {
          const view = editorRef.current.view;
          view.dispatch({
            changes: { from: 0, to: view.state.doc.length, insert: originalSql },
          });
        }
      } else {
        setEditedSql(originalSql);
        setIsEditMode(true);
      }
      setIsFormatted(false);
    } else {
      // Not formatted, format it
      // Store current SQL as original before formatting (in case user edited it)
      originalSqlRef.current = currentSql;
      const formatted = formatSqlQuery(currentSql);
      if (isEditMode) {
        setEditedSql(formatted);
        if (editorRef.current) {
          const view = editorRef.current.view;
          view.dispatch({
            changes: { from: 0, to: view.state.doc.length, insert: formatted },
          });
        }
      } else {
        setEditedSql(formatted);
        setIsEditMode(true);
      }
      setIsFormatted(true);
    }
  };

  const handleEditToggle = () => {
    const newEditMode = !isEditMode;
    setIsEditMode(newEditMode);
    
    if (!newEditMode) {
      // Exiting edit mode - save preference for read-only mode
      try {
        sessionStorage.setItem(EDIT_MODE_PREFIX + blockId, "false");
        // Keep the edited SQL in case user wants to edit again
        sessionStorage.setItem(STORAGE_PREFIX + blockId, editedSql);
        setEditedSql(code); // Reset display to original
      } catch (e) {
        console.warn("Failed to save read-only preference:", e);
      }
    } else {
      // Entering edit mode - initialize with current code or stored edit
      try {
        sessionStorage.setItem(EDIT_MODE_PREFIX + blockId, "true");
        const stored = sessionStorage.getItem(STORAGE_PREFIX + blockId);
        if (stored) {
          setEditedSql(stored);
        } else {
          setEditedSql(code);
        }

        // Ensure the editor is focused when entering edit mode so typing works immediately
        setTimeout(() => {
          if (editorRef.current?.view) {
            editorRef.current.view.focus();
          }
        }, 0);
      } catch (e) {
        console.warn("Failed to save edit mode preference:", e);
      }
    }
  };

  const handleExecute = async () => {
    // This function handles SQL execution from ANY SQL code block:
    // - Successful SQL queries
    // - Failed SQL queries (error cases) - users can edit and re-run
    // - User-modified SQL from the editor
    // The results panel will auto-open to show execution results (success or error)
    const sqlToExecute = isEditMode ? editedSql : code;
    if (!sqlToExecute.trim()) {
      return;
    }

    setIsExecuting(true);
    try {
      const userId = user?.userDetails?.user_id;
      if (!userId) {
        throw new Error("User ID not found. Please log in again.");
      }

      const userDbId = StorageHelper.get("selected_user_db_id");
      if (!userDbId) {
        throw new Error("Database connection not found. Please select a database.");
      }

      const db_type = "snowflake";
      const cleanedSql = sqlToExecute.trim().replace(/;+$/, "").trim();

      const result = await runQuery({
        sql: cleanedSql,
        user_id: userId,
        user_db_id: parseInt(userDbId, 10),
        db_type,
      });

      if (!result?.success) {
        throw new Error(result?.error || result?.message || "Failed to execute SQL query");
      }

      const queryData = Array.isArray(result?.data) ? result.data : [];
      const columnNames =
        Array.isArray(result?.fields) && result.fields.length > 0
          ? result.fields.map((f) => f?.name).filter(Boolean)
          : queryData.length > 0
            ? Object.keys(queryData[0])
            : [];

      setSqlResults({
        success: true,
        data: queryData,
        columns: columnNames,
        row_count: Number.isFinite(result?.rowCount) ? result.rowCount : (queryData.length || 0),
        execution_time: Number.isFinite(result?.executionTime) ? result.executionTime : 0,
        error: "",
        // `query` may be auto-limited; `originalQuery` (when present) is the full SQL.
        sql: result?.query || sqlToExecute,
        originalQuery: result?.originalQuery || result?.query || sqlToExecute,
        source: "execute",
      }, true); // Auto-open panel when user intentionally executes SQL
    } catch (error) {
      const errorMessage = error.message || "Failed to execute SQL query";
      setSqlResults({
        success: false,
        data: [],
        columns: [],
        row_count: 0,
        execution_time: 0,
        error: errorMessage,
        sql: sqlToExecute,
        originalQuery: sqlToExecute,
        source: "execute",
      }, true); // Auto-open panel even on error to show error message
      
      // Ensure edit mode persists after error if user was editing
      if (isEditMode) {
        try {
          sessionStorage.setItem(STORAGE_PREFIX + blockId, editedSql);
          sessionStorage.setItem(EDIT_MODE_PREFIX + blockId, "true");
        } catch (e) {
          console.warn("Failed to preserve edit state after error:", e);
        }
      }
    } finally {
      setIsExecuting(false);
    }
  };

  const handleVerifySql = async () => {
    const sqlToExecute = isEditMode ? editedSql : code;
    if (!sqlToExecute.trim()) return;

    setIsVerifying(true);
    try {
      const userId = user?.userDetails?.user_id;
      if (!userId) throw new Error("User ID not found. Please log in again.");

      const userDbId = StorageHelper.get("selected_user_db_id");
      if (!userDbId) throw new Error("Database connection not found. Please select a database.");

      const cleanedSql = sqlToExecute.trim().replace(/;+$/, "").trim();
      const explainSql = `EXPLAIN ${cleanedSql}`;

      const result = await runQuery({
        sql: explainSql,
        user_id: userId,
        user_db_id: parseInt(userDbId, 10),
        db_type: "snowflake",
      });

      if (!result?.success) {
        throw new Error(result?.error || result?.message || "Failed to run EXPLAIN.");
      }

      const queryData = Array.isArray(result?.data) ? result.data : [];
      const columnNames =
        Array.isArray(result?.fields) && result.fields.length > 0
          ? result.fields.map((f) => f?.name).filter(Boolean)
          : queryData.length > 0
            ? Object.keys(queryData[0])
            : [];

      setSqlResults({
        success: true,
        data: queryData,
        columns: columnNames,
        row_count: Number.isFinite(result?.rowCount) ? result.rowCount : queryData.length,
        execution_time: Number.isFinite(result?.executionTime) ? result.executionTime : 0,
        error: "",
        sql: sqlToExecute,
        source: "verify",
      }, true);
    } catch (error) {
      setSqlResults({
        success: false,
        data: [],
        columns: [],
        row_count: 0,
        execution_time: 0,
        error: error.message || "Failed to verify SQL (EXPLAIN).",
        sql: sqlToExecute,
        source: "verify",
      }, true);
    } finally {
      setIsVerifying(false);
    }
  };

  const onCreateEditor = (view) => {
    editorRef.current = { view };

    // Block paste and text insertion at the DOM level when not in edit mode.
    const handlePaste = (event) => {
      if (!isEditModeRef.current) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const handleBeforeInput = (event) => {
      if (!isEditModeRef.current) {
        if (
          event.inputType &&
          (event.inputType === "insertFromPaste" ||
            event.inputType === "insertFromDrop" ||
            event.inputType.startsWith("insert"))
        ) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    };

    view.dom.addEventListener("paste", handlePaste, true);
    view.dom.addEventListener("beforeinput", handleBeforeInput, true);
  };

  const handleThumbUp = () => {
    setFeedbackError(null);
    setThumbUpComment("");
    setThumbUpModalOpen(true);
  };

  const handleThumbUpModalClose = () => {
    setThumbUpModalOpen(false);
    setFeedbackError(null);
    setThumbUpComment("");
  };

  const handleThumbUpSubmit = async () => {
    const generatedSql = isEditMode ? editedSql : code;
    if (!generatedSql?.trim()) return;
    const raisedBy = StorageHelper.get("userDetails")?.user_id;
    setFeedbackError(null);
    setFeedbackLoading(true);
    try {
      await addGeneratedSqlFeedback({
        question: question ?? "",
        generated_sql: generatedSql.trim(),
        expected_sql: generatedSql.trim(),
        raised_by: raisedBy != null ? String(raisedBy) : "",
        nlp_message_id: nlpMessageId ?? null,
        user_db_id: StorageHelper.get("selected_user_db_id") ?? null,
        feedback: "good",
        comment: thumbUpComment?.trim() || null,
        model_id: generatedByModelId,
      });
      handleThumbUpModalClose();
      setFeedbackSent("up");
      if (nlpMessageId != null && typeof onFeedbackSuccess === "function") {
        onFeedbackSuccess(nlpMessageId, "good");
      }
    } catch (err) {
      setFeedbackError(err.message || "Failed to send feedback");
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleThumbDown = () => {
    const generatedSql = isEditMode ? editedSql : code;
    setExpectedSqlModal(generatedSql ?? "");
    setThumbDownComment("");
    setThumbDownModalOpen(true);
  };

  const handleThumbDownModalClose = () => {
    setThumbDownModalOpen(false);
    setFeedbackError(null);
    setThumbDownComment("");
  };

  const handleThumbDownSubmit = async () => {
    const generatedSql = isEditMode ? editedSql : code;
    if (!generatedSql?.trim()) return;
    const raisedBy = StorageHelper.get("userDetails")?.user_id;
    setFeedbackError(null);
    setFeedbackLoading(true);
    try {
      await addGeneratedSqlFeedback({
        question: question ?? "",
        generated_sql: generatedSql.trim(),
        expected_sql: expectedSqlModal.trim() || null,
        raised_by: raisedBy != null ? String(raisedBy) : "",
        nlp_message_id: nlpMessageId ?? null,
        user_db_id: StorageHelper.get("selected_user_db_id") ?? null,
        feedback: "poor",
        comment: thumbDownComment?.trim() || null,
        model_id: generatedByModelId,
      });
      handleThumbDownModalClose();
      setFeedbackSent("down");
      if (nlpMessageId != null && typeof onFeedbackSuccess === "function") {
        onFeedbackSuccess(nlpMessageId, "poor");
      }
    } catch (err) {
      setFeedbackError(err.message || "Failed to send feedback");
    } finally {
      setFeedbackLoading(false);
    }
  };

  return (
    <Box
      className="sql-code-block-wrapper"
      sx={{
        position: "relative",
        margin: "0 !important",
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: THEME.background.default,
        border: `1px solid ${THEME.border.default} !important`,
        padding: "0 !important",
        display: "block",
        outline: "none !important",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06) !important",
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: "0 2px 6px rgba(0,0,0,0.08) !important",
        },
      }}
    >
      {/* Toolbar (top of editor) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "5px 10px",
          backgroundColor: THEME.background.toolbar,
          borderBottom: `1px solid ${THEME.border.light}`,
          minHeight: "36px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {canEditSql && (
            <Tooltip title={isEditMode ? "Make Read-Only" : "Edit SQL"} arrow>
              <IconButton
                size="small"
                onClick={handleEditToggle}
                disabled={isLoading}
                sx={{
                  color: isEditMode ? THEME.primary : THEME.text.secondary,
                  padding: "4px",
                  borderRadius: "6px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background: isEditMode
                      ? THEME.primaryRgba(0.1)
                      : alpha(THEME.text.secondary, 0.08),
                    color: isEditMode ? THEME.hover : THEME.primary,
                  },
                  "&:disabled": {
                    color: THEME.text.muted,
                    background: "transparent",
                  },
                }}
              >
                <EditIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )}
          {canFormatSql && (
            <Tooltip title={isFormatted ? "Revert Formatting" : "Format SQL"} arrow>
              <IconButton
                size="small"
                onClick={handleFormat}
                disabled={isLoading}
                sx={{
                  color: isFormatted ? THEME.primary : THEME.text.secondary,
                  padding: "4px",
                  borderRadius: "6px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background: isFormatted
                      ? THEME.primaryRgba(0.1)
                      : alpha(THEME.text.secondary, 0.08),
                    color: isFormatted ? THEME.hover : THEME.primary,
                  },
                  "&:disabled": {
                    color: THEME.text.muted,
                    background: "transparent",
                  },
                }}
              >
                <FormatAlignLeftIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )}
          {canExecuteQuery && (
            <Tooltip title="Execute Query" arrow>
              <IconButton
                size="small"
                onClick={handleExecute}
                disabled={isExecuting || isVerifying || isLoading}
                sx={{
                  color: THEME.text.secondary,
                  padding: "4px",
                  borderRadius: "6px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background: THEME.primaryRgba(0.1),
                    color: THEME.primary,
                  },
                  "&:disabled": {
                    color: THEME.text.muted,
                    background: "transparent",
                  },
                }}
              >
                {isExecuting ? (
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      border: `2px solid ${THEME.primary}`,
                      borderTopColor: "transparent",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                ) : (
                  <PlayArrowIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Tooltip>
          )}
          {canVerifySql && (
            <Tooltip title="Verify SQL" arrow>
              <IconButton
                size="small"
                onClick={handleVerifySql}
                disabled={isExecuting || isVerifying || isLoading}
                sx={{
                  color: THEME.text.secondary,
                  padding: "4px",
                  borderRadius: "6px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background: alpha(THEME.text.secondary, 0.08),
                    color: THEME.primary,
                  },
                  "&:disabled": {
                    color: THEME.text.muted,
                    background: "transparent",
                  },
                }}
              >
                {isVerifying ? (
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      border: `2px solid ${THEME.primary}`,
                      borderTopColor: "transparent",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                ) : (
                  <FactCheckIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Tooltip>
          )}
          {canCopySql && (
            <Tooltip title={copied ? "Copied!" : "Copy SQL"} arrow>
              <IconButton
                size="small"
                onClick={handleCopy}
                disabled={copied || isLoading}
                sx={{
                  color: copied ? THEME.success : THEME.text.secondary,
                  padding: "4px",
                  borderRadius: "6px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background: copied
                      ? alpha(THEME.success, 0.1)
                      : alpha(THEME.text.secondary, 0.08),
                    color: copied ? THEME.success : THEME.primary,
                  },
                }}
              >
                {copied ? (
                  <CheckCircleIcon sx={{ fontSize: 17 }} />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: 17 }} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 4 }} />
        {isEditMode && editedSql !== code && (
          <Box
            sx={{
              fontSize: "0.7rem",
              fontWeight: 500,
              color: THEME.warning,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              padding: "2px 6px",
              borderRadius: "4px",
              backgroundColor: alpha(THEME.warning, 0.1),
            }}
          >
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: THEME.warning,
                display: "inline-block",
                animation: "pulse 2s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.6 },
                },
              }}
            />
            Modified
          </Box>
        )}
        {showFeedbackIcons && (canGiveFeedback || canRetry) && !(isEditMode && editedSql !== code) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 1,
                height: 16,
                backgroundColor: THEME.border.light,
                mr: 0.5,
                borderRadius: 1,
              }}
            />
            {messageModelId && (
              <Typography
                variant="caption"
                sx={{
                  color: THEME.text.muted,
                  fontSize: "0.7rem",
                  whiteSpace: "nowrap",
                  mr: 0.5,
                }}
              >
                {`generated by ${messageModelId}`}
              </Typography>
            )}
            {feedbackLoading ? (
              <Tooltip title="Sending feedback…" arrow>
                <IconButton
                  size="small"
                  disabled
                  sx={{
                    padding: "4px",
                    borderRadius: "6px",
                    color: THEME.text.muted,
                    cursor: "default",
                  }}
                >
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      border: `2px solid ${THEME.primary}`,
                      borderTopColor: "transparent",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                </IconButton>
              </Tooltip>
            ) : (
              <>
                {canRetry && showRetryForThisMessage && (
                  <>
                    <Tooltip title={modelOptions?.length > 1 ? "Retry with different model" : "Retry"} arrow>
                      <IconButton
                        size="small"
                        disabled={isLoading}
                        onClick={(e) => {
                          if (modelOptions?.length) {
                            setRetryMenuAnchor(e.currentTarget);
                          } else {
                            onRetry?.(selectedModel);
                          }
                        }}
                        sx={{
                          color: THEME.text.secondary,
                          padding: "4px",
                          borderRadius: "6px",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            background: alpha(THEME.text.secondary, 0.08),
                            color: THEME.primary,
                          },
                        }}
                      >
                        <ReplayIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                    {modelOptions?.length > 0 && (
                      <Menu
                        anchorEl={retryMenuAnchor}
                        open={Boolean(retryMenuAnchor)}
                        onClose={() => setRetryMenuAnchor(null)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        slotProps={{ paper: { sx: { minWidth: 220, mt: 1 } } }}
                      >
                        {modelOptions.map((m) => (
                          <MenuItem
                            key={m.value}
                            onClick={() => {
                              onRetry?.(m.value);
                              setRetryMenuAnchor(null);
                            }}
                            selected={selectedModel === m.value}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                              <span>{m.label ?? m.value}</span>
                              {selectedModel === m.value && (
                                <CheckIcon sx={{ fontSize: 16, color: THEME.primary }} />
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      </Menu>
                    )}
                  </>
                )}
                {canGiveFeedback && feedbackSent !== "down" && (
                  <Tooltip title={feedbackSent === "up" ? "Thanks for your feedback!" : "Good result"} arrow>
                    <IconButton
                      size="small"
                      onClick={handleThumbUp}
                      disabled={feedbackSent === "up" || isLoading}
                      sx={{
                        color: feedbackSent === "up" ? THEME.success : THEME.text.secondary,
                        padding: "4px",
                        borderRadius: "6px",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          background: alpha(THEME.text.secondary, 0.08),
                          color: THEME.primary,
                        },
                        "&:disabled": {
                          color: feedbackSent === "up" ? THEME.success : THEME.text.muted,
                        },
                      }}
                    >
                      <ThumbUpIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {canGiveFeedback && feedbackSent !== "up" && (
                  <Tooltip title={feedbackSent === "down" ? "Thanks for your feedback!" : "Poor result"} arrow>
                    <IconButton
                      size="small"
                      onClick={handleThumbDown}
                      disabled={feedbackSent === "down" || isLoading}
                      sx={{
                        color: feedbackSent === "down" ? THEME.primary : THEME.text.secondary,
                        padding: "4px",
                        borderRadius: "6px",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          background: alpha(THEME.text.secondary, 0.08),
                          color: THEME.primary,
                        },
                        "&:disabled": {
                          color: feedbackSent === "down" ? THEME.primary : THEME.text.muted,
                        },
                      }}
                    >
                      <ThumbDownIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        )}
      </Box>

      {/* Code Editor */}
      <Box
        className="sql-code-block-editor"
        onPaste={(event) => {
          if (!isEditMode) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        sx={{
          margin: "0 !important",
          border: "none !important",
          padding: "0 !important",
          "& .cm-editor": {
            background: isEditMode
              ? THEME.background.default
              : THEME.background.readOnly,
            fontSize: "14px",
            fontFamily:
              "'JetBrains Mono', 'Fira Code', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
            border: "none !important",
            outline: "none",
            margin: "0 !important",
            padding: "0 !important",
            transition: "background-color 0.2s ease",
          },
          "& .cm-editor .cm-scroller": {
            lineHeight: "1.6",
            border: "none !important",
            margin: "0 !important",
            overflowX: "auto",
          },
          "& .cm-editor .cm-content": {
            padding: "12px 16px",
            border: "none !important",
            margin: "0 !important",
            minHeight: "60px",
          },
          "& .cm-focused": {
            outline: "none",
            border: "none !important",
          },
          "& .cm-focused .cm-content": {
            outline: "none",
          },
          "& .cm-theme-light .cm-gutters": {
            backgroundColor: isEditMode
              ? THEME.background.default
              : THEME.background.readOnly,
            borderRight: `1px solid ${THEME.border.light} !important`,
            border: "none !important",
            borderLeft: "none !important",
            borderTop: "none !important",
            borderBottom: "none !important",
            color: THEME.text.muted,
            transition: "background-color 0.2s ease",
          },
          "& .cm-gutters": {
            borderRight: `1px solid ${THEME.border.light} !important`,
            border: "none !important",
            borderLeft: "none !important",
            borderTop: "none !important",
            borderBottom: "none !important",
          },
          "& .cm-lineNumbers .cm-lineNumber": {
            color: THEME.text.muted,
            fontSize: "13px",
            padding: "0 8px 0 12px",
            minWidth: "40px",
          },
          "& .cm-activeLineGutter": {
            backgroundColor: THEME.primaryRgba(0.05),
          },
          "& .cm-activeLine": {
            backgroundColor: "transparent",
          },
          "& .cm-wrapper": {
            border: "none !important",
            margin: "0 !important",
            padding: "0 !important",
          },
          "& .cm-cursor": {
            borderLeftColor: THEME.primary,
            borderLeftWidth: "2px",
          },
          "& .cm-selectionBackground": {
            backgroundColor: THEME.primaryRgba(0.15),
          },
          "& *": {
            boxSizing: "border-box",
          },
        }}
      >
        <CodeMirror
          key={isEditMode ? "sql-editor-editable" : "sql-editor-readonly"}
          value={isEditMode ? editedSql : code}
          onChange={(value) => {
            if (isEditMode) {
              setEditedSql(value);
              // Save immediately to sessionStorage
              try {
                sessionStorage.setItem(STORAGE_PREFIX + blockId, value);
                sessionStorage.setItem(EDIT_MODE_PREFIX + blockId, "true");
              } catch (e) {
                console.warn("Failed to save edit to sessionStorage:", e);
              }
            }
          }}
          height="auto"
          theme="light"
          extensions={[sql(), EditorView.lineWrapping]}
          editable={isEditMode}
          basicSetup={{
            lineNumbers: true,
            highlightActiveLineGutter: true,
            foldGutter: true,
            dropCursor: false,
          }}
          onCreateEditor={onCreateEditor}
        />
      </Box>

      {/* Thumb up: Confirm good SQL modal */}
      {thumbUpModalOpen && (
        <ModalOverlay>
          <ModalContainer
            onClick={(e) => e.stopPropagation()}
            sx={{
              maxWidth: 640,
              width: "90%",
              padding: 0,
              overflow: "hidden",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                px: 2,
                pt: 1.75,
                pb: 1.25,
                borderBottom: `1px solid ${THEME.border.light}`,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: THEME.text.primary,
                  fontSize: "1rem",
                  lineHeight: 1.3,
                }}
              >
                Confirm Good SQL
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: THEME.text.secondary,
                  mt: 0.25,
                  fontSize: "0.75rem",
                }}
              >
                If this SQL correctly answers the question, confirm below to send it to the reviewer and improve future results.
              </Typography>
            </Box>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                backgroundColor: THEME.background.toolbar,
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
              }}
            >
              {question && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: THEME.text.secondary,
                      fontWeight: 600,
                      mb: 0.5,
                      textTransform: "uppercase",
                      letterSpacing: 0.3,
                    }}
                  >
                    Question
                  </Typography>
                  <Box
                    sx={{
                      fontSize: "0.8rem",
                      lineHeight: 1.5,
                      color: THEME.text.primary,
                      backgroundColor: THEME.background.default,
                      borderRadius: "6px",
                      border: `1px solid ${THEME.border.light}`,
                      padding: "6px 8px",
                      maxHeight: 80,
                      overflowY: "auto",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {question}
                  </Box>
                </Box>
              )}
              {code && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: THEME.text.secondary,
                      fontWeight: 600,
                      mb: 0.5,
                      textTransform: "uppercase",
                      letterSpacing: 0.3,
                    }}
                  >
                    Generated SQL{(messageModelId || selectedModel) ? ` (${messageModelId || selectedModel})` : ""}
                  </Typography>
                  <Box
                    sx={{
                      fontSize: "0.75rem",
                      lineHeight: 1.5,
                      color: THEME.text.primary,
                      backgroundColor: THEME.background.default,
                      borderRadius: "6px",
                      border: `1px solid ${THEME.border.light}`,
                      padding: "6px 8px",
                      maxHeight: 160,
                      overflowY: "auto",
                      fontFamily:
                        "'JetBrains Mono', 'Fira Code', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {code}
                  </Box>
                </Box>
              )}
              <Box sx={{ mt: 1.5 }}>
                <TextField
                  label="Comment"
                  placeholder="Briefly describe why this SQL is good"
                  value={thumbUpComment}
                  onChange={(e) => setThumbUpComment(e.target.value)}
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  maxRows={4}
                />
              </Box>
            </Box>
            {feedbackError && (
              <Box sx={{ px: 2, py: 0.75 }}>
                <Typography variant="caption" sx={{ color: "error.main", fontSize: "0.75rem" }}>
                  {feedbackError}
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                px: 2,
                py: 1.5,
                borderTop: `1px solid ${THEME.border.light}`,
                backgroundColor: THEME.background.default,
              }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={handleThumbUpModalClose}
                disabled={feedbackLoading}
                sx={{
                  textTransform: "none",
                  fontSize: "0.8125rem",
                  borderColor: THEME.border.default,
                  color: THEME.text.secondary,
                  "&:hover": {
                    borderColor: THEME.primary,
                    color: THEME.primary,
                    backgroundColor: THEME.primaryRgba(0.06),
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleThumbUpSubmit}
                disabled={feedbackLoading}
                sx={{
                  backgroundColor: THEME.primary,
                  textTransform: "none",
                  fontSize: "0.8125rem",
                  "&:hover": { backgroundColor: THEME.hover },
                }}
              >
                {feedbackLoading ? (
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        border: "2px solid rgba(255,255,255,0.3)",
                        borderTopColor: "#fff",
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                        "@keyframes spin": {
                          "0%": { transform: "rotate(0deg)" },
                          "100%": { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                    Submitting…
                  </Box>
                ) : (
                  "Confirm"
                )}
              </Button>
            </Box>
          </ModalContainer>
        </ModalOverlay>
      )}

      {/* Thumb down: Expected SQL modal */}
      {thumbDownModalOpen && (
        <ModalOverlay>
          <ModalContainer
            onClick={(e) => e.stopPropagation()}
            sx={{
              maxWidth: 640,
              width: "90%",
              padding: 0,
              overflow: "hidden",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                px: 2,
                pt: 1.75,
                pb: 1.25,
                borderBottom: `1px solid ${THEME.border.light}`,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: THEME.text.primary,
                  fontSize: "1rem",
                  lineHeight: 1.3,
                }}
              >
                What the SQL Should Be
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: THEME.text.secondary,
                  mt: 0.25,
                  fontSize: "0.75rem",
                }}
              >
                Edit the SQL to match your expectation and submit it for review.
              </Typography>
            </Box>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                backgroundColor: THEME.background.toolbar,
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                "& .cm-editor": {
                  fontSize: "13px",
                  fontFamily:
                    "'JetBrains Mono', 'Fira Code', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
                  backgroundColor: THEME.background.default,
                  border: `1px solid ${THEME.border.light}`,
                  borderRadius: "6px",
                  overflow: "hidden",
                },
                "& .cm-scroller": { minHeight: 180 },
                "& .cm-content": { padding: "10px 12px" },
                "& .cm-gutters": {
                  backgroundColor: THEME.background.readOnly,
                  borderRight: `1px solid ${THEME.border.light}`,
                },
              }}
            >
              {question && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: THEME.text.secondary,
                      fontWeight: 600,
                      mb: 0.5,
                      textTransform: "uppercase",
                      letterSpacing: 0.3,
                    }}
                  >
                    Question
                  </Typography>
                  <Box
                    sx={{
                      fontSize: "0.8rem",
                      lineHeight: 1.5,
                      color: THEME.text.primary,
                      backgroundColor: THEME.background.default,
                      borderRadius: "6px",
                      border: `1px solid ${THEME.border.light}`,
                      padding: "6px 8px",
                      maxHeight: 80,
                      overflowY: "auto",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {question}
                  </Box>
                </Box>
              )}
              {code && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: THEME.text.secondary,
                      fontWeight: 600,
                      mb: 0.5,
                      textTransform: "uppercase",
                      letterSpacing: 0.3,
                    }}
                  >
                    Generated SQL{(messageModelId || selectedModel) ? ` (${messageModelId || selectedModel})` : ""}
                  </Typography>
                  <Box
                    sx={{
                      fontSize: "0.75rem",
                      lineHeight: 1.5,
                      color: THEME.text.primary,
                      backgroundColor: THEME.background.default,
                      borderRadius: "6px",
                      border: `1px solid ${THEME.border.light}`,
                      padding: "6px 8px",
                      maxHeight: 140,
                      overflowY: "auto",
                      fontFamily:
                        "'JetBrains Mono', 'Fira Code', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {code}
                  </Box>
                </Box>
              )}
              <CodeMirror
                value={expectedSqlModal}
                onChange={setExpectedSqlModal}
                height="180px"
                theme="light"
                extensions={[sql(), EditorView.lineWrapping]}
                editable
                basicSetup={{
                  lineNumbers: true,
                  highlightActiveLineGutter: true,
                  foldGutter: false,
                }}
              />
              <Box sx={{ mt: 1.5 }}>
                <TextField
                  label="Comment"
                  placeholder="Briefly describe what is wrong or how this SQL should change"
                  value={thumbDownComment}
                  onChange={(e) => setThumbDownComment(e.target.value)}
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  maxRows={4}
                />
              </Box>
            </Box>
            {feedbackError && (
              <Box sx={{ px: 2, py: 0.75 }}>
                <Typography variant="caption" sx={{ color: "error.main", fontSize: "0.75rem" }}>
                  {feedbackError}
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                px: 2,
                py: 1.5,
                borderTop: `1px solid ${THEME.border.light}`,
                backgroundColor: THEME.background.default,
              }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={handleThumbDownModalClose}
                disabled={feedbackLoading}
                sx={{
                  textTransform: "none",
                  fontSize: "0.8125rem",
                  borderColor: THEME.border.default,
                  color: THEME.text.secondary,
                  "&:hover": {
                    borderColor: THEME.primary,
                    color: THEME.primary,
                    backgroundColor: THEME.primaryRgba(0.06),
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleThumbDownSubmit}
                disabled={feedbackLoading}
                sx={{
                  backgroundColor: THEME.primary,
                  textTransform: "none",
                  fontSize: "0.8125rem",
                  "&:hover": { backgroundColor: THEME.hover },
                }}
              >
                {feedbackLoading ? (
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        border: "2px solid rgba(255,255,255,0.3)",
                        borderTopColor: "#fff",
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                        "@keyframes spin": {
                          "0%": { transform: "rotate(0deg)" },
                          "100%": { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                    Submitting…
                  </Box>
                ) : (
                  "Submit"
                )}
              </Button>
            </Box>
          </ModalContainer>
        </ModalOverlay>
      )}
    </Box>
  );
};

export default SqlCodeBlock;

